import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";

import AuthShell from "./_AuthShell";
import { Button } from "../../components/DCKeeper";
import { passwordApi } from "../../helpers/dck_helper";
import { passwordIssue, PASSWORD_MIN_LENGTH as MIN_LENGTH } from "../../helpers/passwordRules";

/**
 * 設定新密碼（/reset-password?token=...&guid=...,ticket 12→09；網址與參數名照後端信件連結,2026-10-20）。
 * 舊網址 /auth-reset-password?token=...&customerGuid=... 由路由轉過來,兩種參數名都認;
 * 打 API 時一律照 swagger 送 customerGuid。
 *
 * 走真後端契約:信箱連結帶兩把鑰匙(token+customerGuid),進頁先以
 * POST PasswordRecoveryConfirm 驗證(404=失效),提交走 PasswordRecoveryConfirmPOST。
 * 失效就直接講清楚並給「重新申請」的入口,不要讓使用者把新密碼打完才被打回票。
 * 版面沿用模板 auth 頁(與 /auth-login、/auth-recoverpw 同一個外殼)。
 * 密碼規則與後端同一條(helpers/passwordRules),前端先擋是為了少一次來回。
 */
const ResetPassword = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const token = query.get("token") || "";
  const customerGuid = query.get("guid") || query.get("customerGuid") || "";

  const [checking, setChecking] = useState(true);
  const [tokenOk, setTokenOk] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!token || !customerGuid) {
      setTokenOk(false);
      setChecking(false);
      return undefined;
    }
    passwordApi
      .recoveryConfirm({ token, customerGuid })
      // disable_password_changing=true 是後端明說「此帳號不可自行改密」→ 同失效處理
      .then((m) => { if (alive) setTokenOk(!(m && m.disable_password_changing)); })
      .catch(() => { if (alive) setTokenOk(false); })
      .finally(() => { if (alive) setChecking(false); });
    return () => { alive = false; };
  }, [token, customerGuid]);

  const submit = async (e) => {
    e.preventDefault();
    const issue = passwordIssue(password);
    if (issue) { setError(issue); return; }
    if (password !== confirm) { setError("兩次輸入的密碼不一致。"); return; }
    setError(""); setBusy(true);
    try {
      await passwordApi.recoveryConfirmSubmit(
        { token, customerGuid },
        { new_password: password, confirm_new_password: confirm }
      );
      setDone(true);
    } catch (err) {
      // api_helper 的攔截器 reject 的是「已轉譯的字串」,不是 Error
      setError(typeof err === "string" ? err : "重設失敗,請重新申請。");
      setTokenOk(false); // 後端說連結不能用了,就別再讓人送第二次
    } finally {
      setBusy(false);
    }
  };

  return (
    <React.Fragment>
      <Helmet><title>設定新密碼 | DC Keeper</title></Helmet>
      <AuthShell>
        <div className="text-center mt-4">
          <h5 className="mb-2 fw-bold">設定新密碼</h5>
          <p className="text-muted">密碼至少 {MIN_LENGTH} 碼,需包含大小寫英文與數字</p>
        </div>

        {checking ? (
          <div className="text-center text-muted my-4">確認連結中…</div>
        ) : done ? (
          <div className="my-4">
            <div className="alert alert-success text-center" role="alert">
              密碼已重設成功，請以新密碼登入；若非您本人操作，請立即聯繫帳號管理者。
            </div>
            <Link to="/auth-login" className="btn btn-primary w-100">前往登入</Link>
          </div>
        ) : !tokenOk ? (
          <div className="my-4">
            <div className="alert alert-danger text-center" role="alert">
              這個重設連結已失效（連結只在寄出後 30 分鐘內有效,且只能用一次）。
            </div>
            <Link to="/auth-recoverpw" className="btn btn-primary w-100">重新申請重設連結</Link>
          </div>
        ) : (
          <form className="mt-4 pt-2" onSubmit={submit}>
            <div className="form-floating form-floating-custom mb-4">
              <input
                type="password" id="input-password" required className="form-control"
                placeholder="請輸入新密碼"
                value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }}
              />
              <label htmlFor="input-password">新密碼</label>
              <div className="form-floating-icon"><i className="bx bx-lock" /></div>
            </div>
            <div className="form-floating form-floating-custom mb-4">
              <input
                type="password" id="input-confirm" required className="form-control"
                placeholder="請再輸入一次"
                value={confirm} onChange={(e) => { setConfirm(e.target.value); setError(""); }}
              />
              <label htmlFor="input-confirm">再輸入一次</label>
              <div className="form-floating-icon"><i className="bx bx-lock-alt" /></div>
            </div>
            {error ? <div className="text-danger small mb-3">{error}</div> : null}
            <Button variant="primary" type="submit" block loading={busy}>更新密碼</Button>
          </form>
        )}

        <div className="mt-4 pt-3 text-center">
          <p className="text-muted mb-0">
            想起密碼了？<Link to="/auth-login" className="text-primary fw-semibold"> 直接登入 </Link>
          </p>
        </div>
      </AuthShell>
    </React.Fragment>
  );
};

export default ResetPassword;
